import { useQuery } from '@tanstack/react-query'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router'

import { useErrorHandler } from '../../hooks/useErrorHandler'
import { usePageTitle } from '../../hooks/usePageTitle'
import { Field } from '../../models/Field'
import { RecordFactory } from '../../models/RecordFactory'
import { useHistoryTrackerContext } from '../../providers/HistoryTrackerProvider'
import { useOnboardingContext } from '../../providers/OnboardingProvider'
import { getEditionID } from '../../utils/users'
import { Icon } from '../Icon'
import { PSApplication } from '../app/PSApplication'
import Button from '../app/design/Button'
import Heading from '../app/design/Heading'
import Paragraph from '../app/design/Paragraph'
import { SelectList } from '../common/SelectList'
import { WorkflowSelectListSkeleton } from '../common/skeletons/WorkflowSelectListSkeleton'
import { FieldModal } from '../items/FieldModal'
import { WorkflowPage } from '../workflows/WorkflowPage'

type OnboardingIntakeField = {
    field: Field
    selected: boolean
}

const mergeFields = (defaults: Field[], saved: Field[]): OnboardingIntakeField[] => {
    return defaults.map((d) => {
        const foundFromSaved = saved.find((c) => c.editionField === d.id)
        if (foundFromSaved) {
            return { field: foundFromSaved, selected: true }
        }
        return { field: d, selected: false }
    })
}

export function SetupOnboardingClientIntakePage() {
    const user = PSApplication.me()
    const owner = PSApplication.owner()
    const editionID = getEditionID(user)
    const { logAndDisplayError } = useErrorHandler()
    usePageTitle('Intake')
    const { goBack } = useHistoryTrackerContext()

    const { advanceOnboardingState } = useOnboardingContext()

    const { data, isLoading } = useQuery({
        queryKey: ['onboarding', 'intake', editionID],
        queryFn: async () => {
            const [edition, fields] = await Promise.all([
                RecordFactory.getInstance().loadEdition(getEditionID(user)),
                RecordFactory.getInstance().loadFields(
                    owner.id!,
                    undefined,
                    true, // client
                    false,
                    false,
                    false,
                    false,
                    false,
                    false,
                    false
                ),
            ])

            const fieldsToSelect = mergeFields(edition?.defaultIntakeFields ?? [], fields)

            return { edition, fields: fieldsToSelect }
        },
    })

    const [selectableFields, setSelectableFields] = useState(data?.fields ?? [])
    const [fieldToEdit, setFieldToEdit] = useState<Field | null>(null)
    const navigate = useNavigate()
    const [isSaving, setIsSaving] = useState(false)

    useEffect(() => {
        if (data) {
            setSelectableFields(data.fields)
        }
    }, [data])

    const selectedFieldsListItems = useMemo(() => {
        return selectableFields.map((f) => ({
            id: f.field.id ?? '',
            title: f.field.label ?? '',
            subtitle: f.field.type ? Field.FIELD_TYPES.get(f.field.type) : '',
            tertiaryView: <Icon icon="form2.large" background="round" />,
            selected: f.selected,
            button: f.selected
                ? {
                      title: 'Edit',
                      onClick: () => setFieldToEdit(f.field),
                  }
                : undefined,
        }))
    }, [selectableFields])

    const onDoneEditing = useCallback(
        (field: Field) => {
            field.editionField = field.id
            setSelectableFields((s) =>
                s.map((f) =>
                    f.field.id === field.editionField ? { field: field, selected: true } : f
                )
            )
            setFieldToEdit(null)
        },
        [selectableFields]
    )

    const onNextHandler = async () => {
        setIsSaving(true)
        const clonedFields = selectableFields
            .filter((f) => f.selected)
            .map((f) => {
                const clone = f.field.clone()
                clone.edition = editionID
                clone.editionField = f.field.id
                clone.id = undefined
                return clone
            })

        const results = await RecordFactory.getInstance().sendOnboardingIntake(clonedFields)
        setIsSaving(false)
        if (!results.ok) {
            logAndDisplayError(results.error)
            return
        }

        await advanceOnboardingState()
    }

    if (isLoading || !data) {
        return (
            <WorkflowSelectListSkeleton
                title="Intake"
                subtitle="Add and edit the questions that you'd like all new clients to answer."
                onClose={() => {}}
            />
        )
    }

    return (
        <>
            <WorkflowPage
                onClose={() => navigate('/')}
                onBack={() => goBack()}
                backButtonTitle={'Back'}
                footerContent={
                    <div className="flex justify-end">
                        <Button
                            intent="primary"
                            type="submit"
                            disabled={isSaving}
                            isLoading={isSaving}
                            onClick={onNextHandler}
                        >
                            Next
                        </Button>
                    </div>
                }
            >
                <Heading size="xl" className="mt-[5px]">
                    Your intake questions
                </Heading>
                <Paragraph className="mt-4 max-w-[580px] text-center text-gray-600">
                    Add and edit the questions that you'd like all new clients to answer.
                </Paragraph>
                <WorkflowPage.Card className="mt-12">
                    <SelectList
                        selectionType="multi"
                        items={selectedFieldsListItems}
                        selectedItems={selectedFieldsListItems.filter((f) => f.selected)}
                        onSelectItems={(items) => {
                            const selectedFieldIDs = items.map((f) => f.id)
                            setSelectableFields((s) =>
                                s.map((f) =>
                                    selectedFieldIDs.includes(f.field.id ?? '')
                                        ? { field: f.field, selected: true }
                                        : { field: f.field, selected: false }
                                )
                            )
                        }}
                    />
                </WorkflowPage.Card>
            </WorkflowPage>
            {fieldToEdit && (
                <FieldModal
                    isOpen
                    title={`Edit question`}
                    onClose={() => setFieldToEdit(null)}
                    field={fieldToEdit}
                    onDone={onDoneEditing}
                    allowTypeModification={false}
                />
            )}
        </>
    )
}
